import {
  Component,
  Input,
  OnChanges,
  SimpleChanges,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  OnInit,
  Output,
  EventEmitter,
  OnDestroy,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { MojoFeatureExtensionsService } from 'mojo-feature-extensions';
import { Observable, ReplaySubject, combineLatest, BehaviorSubject, of, Subscription } from 'rxjs';
import { map, tap, shareReplay, switchMap, take, filter } from 'rxjs/operators';

import { AppFeatureExtensions } from '@app/app-feature-extensions.config';
import {
  ChangeCandidateStatusModalComponent,
  ChangeCandidateStatusModalData,
} from '@modules/assigns/components/change-candidate-status-modal/change-candidate-status-modal.component';
import {
  getAssociationPhase,
  getAssociationStatusConfigurationsShortList,
  getAssociationStatusMoveForwardSteps,
  getAssociationStepConfig,
  getFinalPhase,
  getNextAvailablePhases,
  getPreviousAvailablePhases,
} from '@modules/assigns/store';
import { Association, AssociationStatusConfiguration } from '@shared/models';
import { CustomStepperColors } from '@shared/stepper/stepper.component';
import { MojoBatchService } from 'mojo-batch';

@Component({
  selector: 'mojo-dynamic-progress',
  templateUrl: './dynamic-progress.component.html',
  styleUrls: ['./dynamic-progress.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DynamicProgressComponent implements OnChanges, OnInit, OnDestroy {
  @Input() association: Association;
  @Input() expandedView = true;
  // TODO: probably could be retrived from association after some refactoring
  @Input() isDirectApplicant = false;
  @Input() widget = false;
  @Input() disableStepper = false;
  @Input() fromDashboardLanding = false;
  @Output() candidateMoved = new EventEmitter();

  selectedPhaseId$ = new ReplaySubject<string>(1);
  private selectedStateId$ = new ReplaySubject<string>(1);
  private expandedView$ = new BehaviorSubject<boolean>(this.expandedView);
  subs = new Subscription();
  moveForwardOnly$: Observable<boolean> = this.featureExtensionsService.hasFeatureExtension(
    AppFeatureExtensions.ASSOCIATION_FORWARD_ONLY,
  );

  private successColors: CustomStepperColors = {
    primary: '#30DA5A',
    lightPrimary: '#EAFBEE',
  };

  selectedStateCustomColors$: Observable<Partial<CustomStepperColors>>;

  private selectedStateConfig$ = combineLatest([this.selectedPhaseId$, this.selectedStateId$]).pipe(
    filter((_) => !!this.association),
    switchMap(([selectedPhaseId, selectedStateId]) =>
      this.store.select(getAssociationStepConfig, {
        jobId: this.association.reqId,
        configurationStateId: selectedStateId ? selectedStateId : selectedPhaseId,
      }),
    ),
    shareReplay(1),
  );

  displayedStepsConfiguration$: Observable<AssociationStatusConfiguration[]> = combineLatest([
    this.selectedStateConfig$,
    this.expandedView$,
    this.featureExtensionsService.hasFeatureExtension(AppFeatureExtensions.ORACLE_ATS),
  ]).pipe(
    filter((_) => !!this.association),
    switchMap(([{ phaseConfig, stateConfig }, expandedView, hasFeatureExtension]) => {
      const atsName = hasFeatureExtension ? 'Oracle' : 'ATS';
      return (
        expandedView
          ? this.getAssociationConfigurationShortListed(phaseConfig)
          : this.store.select(getAssociationStatusMoveForwardSteps, {
              jobId: this.association.reqId,
              selectedPhaseId: phaseConfig.id,
              limit: 1,
            })
      ).pipe(
        map((steps) => !!steps && this.prepareSteps(steps, stateConfig, atsName)),
        tap(() => this.cd.markForCheck()),
      );
    }),
    shareReplay(1),
  );

  selectedStateLabel$ = this.selectedStateConfig$.pipe(
    filter((_) => !!this.association),
    map(({ phaseConfig, stateConfig }) => {
      const dateString = this.association.associationStatuses.find(
        ({ status }) => status === phaseConfig.id,
      )?.createdDate;
      const createdDate = !!dateString
        ? new Date(dateString).toLocaleDateString('en-US')
        : new Date(this.association.lastModifiedDate).toLocaleDateString('en-US');
      return stateConfig
        ? this.widget
          ? `${phaseConfig.label} > ${stateConfig.label} \n ${createdDate}`
          : `State: ${stateConfig.label} \n ${createdDate}`
        : this.widget
        ? `${phaseConfig.label} \n ${createdDate}`
        : createdDate;
    }),
    shareReplay(1),
  );

  prepareSteps = (steps, stateConfig, atsName) => [
    ...steps?.map((step) => {
      const dateString = this.association.associationStatuses
        .filter(({ status }) => status === step.id)
        .at(-1)?.createdDate;
      const createdDate = !!dateString ? new Date(dateString).toLocaleDateString('en-US') : false;
      return {
        ...step,
        icon: !!stateConfig ? stateConfig.icon : step.icon,
        value: step.id,
        isClickable: !step.blockForward,
        createdDate,
        inactiveLabel: step.blockForward
          ? this.widget
            ? step.label + '\n(continue workflow in ' + atsName + ')'
            : 'Continue workflow in ' + atsName
          : this.widget
          ? step.label
          : '',
      };
    }),
  ];

  directApplicant = AppFeatureExtensions.ASSOCIATION_DIRECT_APPLICANT;

  constructor(
    private store: Store,
    private dialog: MatDialog,
    private cd: ChangeDetectorRef,
    private featureExtensionsService: MojoFeatureExtensionsService,
    private mojoBatchService: MojoBatchService,
  ) {}

  ngOnInit() {
    this.selectedStateCustomColors$ = combineLatest([
      this.store.select(getFinalPhase, { jobId: this.association?.reqId }),
      this.selectedPhaseId$,
    ]).pipe(
      filter(([finalPhaseConfig, _]) => !!finalPhaseConfig),
      map(([finalPhaseConfig, selectedPhaseId]) =>
        finalPhaseConfig.id === selectedPhaseId ? this.successColors : {},
      ),
      shareReplay(1),
    );
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.association && changes.association.currentValue) {
      if (
        changes.association?.currentValue?.status !== changes.association?.previousValue?.status
      ) {
        this.selectedPhaseId$.next(changes.association.currentValue.status);
      }

      if (
        changes.association?.currentValue?.subStatus !==
        changes.association?.previousValue?.subStatus
      ) {
        this.selectedStateId$.next(changes.association.currentValue.subStatus);
      }
    }
    if (changes.expandedView?.currentValue !== changes.expandedView?.previousValue) {
      this.expandedView$.next(changes.expandedView.currentValue);
    }
  }

  getAssociationConfigurationShortListed(selectedPhase: AssociationStatusConfiguration) {
    return this.store
      .select(getAssociationStatusConfigurationsShortList, {
        jobId: this.association.reqId,
      })
      .pipe(
        map((configurations) => {
          if (!!selectedPhase) {
            const { id: selectedPhaseId = '', position = 0 } = selectedPhase;
            if (!selectedPhaseId || configurations.some(({ id }) => id === selectedPhaseId)) {
              return configurations;
            }
            if (position === 0) {
              return [selectedPhase, ...configurations];
            }
            return [
              ...configurations.slice(0, position),
              selectedPhase,
              ...configurations.slice(position + 1),
            ];
          }
        }),
      );
  }

  onSelection(step: AssociationStatusConfiguration) {
    if (!this.disableStepper) {
      this.mojoBatchService.clearSelection();
      this.selectedStateConfig$
        .pipe(
          filter((_) => !!this.association),
          switchMap((selectedConfiguration) =>
            combineLatest([
              this.getAvailablePhases$(selectedConfiguration.phaseConfig.id),
              of(selectedConfiguration),
            ]),
          ),
          take(1),
        )
        .subscribe(([availableStepsConfiguration, selectedConfiguration]) => {
          if (availableStepsConfiguration.find(({ id }) => id === step.id)) {
            this.openModal(step, availableStepsConfiguration, selectedConfiguration);
          }
        });
    }
  }

  private getAvailablePhases$(selectedPhaseId): Observable<AssociationStatusConfiguration[]> {
    return combineLatest([
      this.store.select(getNextAvailablePhases, {
        jobId: this.association.reqId,
        selectedPhaseId,
      }),
      this.store.select(getPreviousAvailablePhases, {
        jobId: this.association.reqId,
        selectedPhaseId,
      }),
      this.moveForwardOnly$,
    ]).pipe(
      map(([nextPhases, previousPhases, moveForwardOnly]) =>
        moveForwardOnly ? nextPhases : [...previousPhases, ...nextPhases],
      ),
    );
  }

  private openModal(step, availableSteps, selectedStep) {
    const initialStateTemp =
      step.children.length > 0
        ? step.id === selectedStep.phaseConfig.id
          ? selectedStep.stateConfig.id
          : step.children[0].id
        : null;
    const dialog = this.dialog.open<
      ChangeCandidateStatusModalComponent,
      ChangeCandidateStatusModalData
    >(ChangeCandidateStatusModalComponent, {
      panelClass: 'medium-size-modal',
      disableClose: true,
      data: {
        jobId: this.association.reqId,
        candidateId: this.association.candidateId,
        initialPhase: step.id,
        initialState: initialStateTemp,
        availableSteps,
        fromDashboardLanding: this.fromDashboardLanding,
        shouldButtonBeEnabledForOnInit: step.id !== selectedStep.phaseConfig.id,
      },
    });

    this.subs.add(
      dialog?.afterClosed().subscribe((res) => {
        if (res.fromDashboardLanding) {
          this.candidateMoved.emit();
        }
      }),
    );
  }

  wasVisited(checkedStateId) {
    if (this.association && this.association.associationStatuses.length > 0) {
      return of(
        this.association.associationStatuses.some(({ status }) => status === checkedStateId),
      );
    }

    return combineLatest([
      this.store.select(getAssociationPhase, {
        jobId: this.association.reqId,
        configurationStateId: checkedStateId,
      }),
      this.selectedStateConfig$,
    ]).pipe(
      map(([checkedPhase, selectedPhase]) => checkedPhase.phaseIndex <= selectedPhase.phaseIndex),
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }
}
